import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, ShieldCheck, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { DISCLAIMER } from "../../lib/api";

export const DisclaimerBanner = () => (
  <div data-testid="disclaimer-banner" className="no-print bg-[#FFFBEB] border-b border-amber-300/60 text-amber-900 text-xs sm:text-[13px] px-4 py-2 text-center">
    {DISCLAIMER}
  </div>
);

const links = [
  { to: "/shop", label: "Shop", id: "nav-shop-link" },
  { to: "/questionnaire", label: "Build my plan", id: "nav-questionnaire-btn" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  const cls = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-[#0D9488]" : "text-slate-700 hover:text-[#0D9488]"}`;

  return (
    <header className="no-print sticky top-0 z-40 backdrop-blur-xl bg-[#F8F7F4]/85 border-b border-[#E4DFD4]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex flex-col items-start gap-1.5">
          <img src="/logo-black.png" alt="CuraWaves" className="h-[15px] sm:h-[19px] w-auto" />
          <span className="font-mono font-extrabold text-[11px] sm:text-[14px] uppercase tracking-[0.2em] text-[#0F766E] leading-none">WaveTherapy.ai</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => <NavLink key={l.to} to={l.to} data-testid={l.id} className={cls}>{l.label}</NavLink>)}
          <a href="/#how" data-testid="nav-how-link" className="text-sm font-medium text-slate-700 hover:text-[#0D9488] transition-colors">How it works</a>
          {user && <NavLink to="/dashboard" data-testid="nav-dashboard-link" className={cls}>My dashboard</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" data-testid="nav-admin-link" className={cls}><span className="inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4" />Admin</span></NavLink>}
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <button data-testid="nav-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="hidden md:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0D9488] px-3 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="hidden md:inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#0D9488] px-3 py-2 transition-colors">
              <User className="w-4 h-4" /> Sign in
            </Link>
          )}
          <button data-testid="nav-cart-btn" onClick={() => setOpen(true)} className="relative inline-flex items-center gap-2 rounded-full bg-[#0B1013] text-white px-4 py-2 text-sm hover:bg-[#0F766E] transition-colors">
            <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Cart</span>
            {count > 0 && <span data-testid="nav-cart-count" className="font-mono text-xs bg-[#06B6D4] text-[#0B1013] rounded-full px-1.5">{count}</span>}
          </button>
          <button className="md:hidden p-2" data-testid="nav-mobile-toggle" onClick={() => setMobile(!mobile)}>{mobile ? <X /> : <Menu />}</button>
        </div>
      </div>
      {mobile && (
        <div className="md:hidden border-t border-[#E4DFD4] px-5 py-4 flex flex-col gap-3 bg-[#F8F7F4]" onClick={() => setMobile(false)}>
          {links.map((l) => <NavLink key={l.to} to={l.to} className={cls}>{l.label}</NavLink>)}
          {user && <NavLink to="/dashboard" className={cls}>My dashboard</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" className={cls}>Admin</NavLink>}
          {user ? <button className="text-left text-sm" onClick={logout}>Sign out</button> : <NavLink to="/login" className={cls}>Sign in</NavLink>}
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="no-print bg-[#0B1013] text-slate-300 mt-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <img src="/logo-white.png" alt="CuraWaves" data-testid="footer-logo" className="h-6 w-auto" />
        <p className="mt-3 text-sm text-slate-400 max-w-sm">Wave Therapy square-wave frequency generators, personalized AI planning and health coaching. Proudly made in the USA.</p>
      </div>
      <div className="md:col-span-3 text-sm space-y-2">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#06B6D4] mb-3">Explore</div>
        <Link to="/shop" className="block hover:text-white">Shop devices</Link>
        <Link to="/questionnaire" className="block hover:text-white">Build my plan</Link>
        <Link to="/dashboard" className="block hover:text-white">My dashboard</Link>
        <a href="https://curawaves.com" target="_blank" rel="noreferrer" className="block hover:text-white">curawaves.com</a>
      </div>
      <div className="md:col-span-4 text-xs text-slate-400 leading-relaxed space-y-2" data-testid="footer-disclaimer">
        <p>{DISCLAIMER}</p>
        <p>These statements have not been evaluated by the FDA. Condition names are shorthand for frequency channels and are used for convenience only. Information on this site is not a substitute for medical advice — consult your healthcare provider for medical concerns and never leave your current medical protocol.</p>
        <p>Do not use when pregnant, or with a pacemaker or any implanted electronic medical device. Drink 8 oz of water before and after every session.</p>
      </div>
    </div>
    <div className="border-t border-white/10 text-center text-xs text-slate-500 py-5">© {new Date().getFullYear()} CuraWaves. All rights reserved.</div>
  </footer>
);
